/**
 * src/lib/api/minioUpload.js — direct-to-MinIO large voice upload (client side).
 *
 * Files larger than the free-plan Supabase 50 MB Storage cap upload straight to
 * MinIO on the Hetzner box: ask Vercel (authed) for a short-lived presigned PUT
 * URL (voice.upload_url runs the role/scope authz + server-decides the object
 * key), then PUT the file directly with XHR byte-progress. Bypasses both the
 * Supabase cap AND Vercel's 4.5 MB function-body limit (client -> MinIO direct).
 *
 * Seams (apiFetchImpl, xhrImpl) are injectable for tests; prod uses apiFetch +
 * window.XMLHttpRequest. XHR (not fetch) is required for upload.onprogress.
 */
import { apiFetch, API_BASE } from './client';
import { putToPresignedUrl, reportUploadFailure } from './presignedPut';

const VOICE = `${API_BASE}/ai`;

// The PUT transport and its failure diagnosis live in ./presignedPut (TICKET-476) so
// every upload surface shares one implementation and one spine step. Re-exported
// because callers and tests already import them from here.
export { putToPresignedUrl, reportUploadFailure };

/**
 * Ask Vercel for a presigned PUT URL + server-decided object key.
 * @returns {Promise<{upload_url:string, object_key:string, bucket:string, storage_backend:string, expires_in:number}>}
 */
export async function requestUploadUrl({ scopeType, scopeId, file, idempotencyKey, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  const body = {
    action: 'voice.upload_url',
    scope_type: scopeType,
    scope_id: scopeId,
    file_name: file.name,
    file_size_bytes: file.size,
  };
  if (scopeType === 'project') body.project_id = scopeId; // back-compat alias
  if (idempotencyKey) body.idempotency_key = idempotencyKey;
  const res = await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(body) });
  if (!res || !res.upload_url || !res.object_key) {
    throw new Error('upload_url request failed (no presigned URL returned)');
  }
  return res;
}

/**
 * Ask Vercel for a short-TTL presigned GET of a voice upload's ORIGINAL audio (T5).
 * The server loads the row, runs the per-role read gate, and presigns — the
 * client never supplies a path (no IDOR). Works for MinIO-archived (hetzner)
 * uploads; supabase uploads 404 once the original was cleaned up post-transcription.
 * @returns {Promise<{download_url:string, file_name?:string, expires_in?:number}>}
 */
export async function requestDownloadUrl({ uploadId, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  const res = await fetchImpl(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.download_url', upload_id: uploadId }),
  });
  if (!res || !res.download_url) {
    throw new Error('download_url request failed (no presigned URL returned)');
  }
  return res;
}

/**
 * Full client flow: presign -> PUT -> return the key the server registers.
 * @returns {Promise<{object_key:string, storage_backend:string, bucket:string}>}
 */
export async function uploadViaPresigned({ file, scopeType, scopeId, idempotencyKey, onProgress, apiFetchImpl, xhrImpl }) {
  const meta = await requestUploadUrl({ scopeType, scopeId, file, idempotencyKey, apiFetchImpl });
  await putToPresignedUrl({ url: meta.upload_url, file, onProgress, xhrImpl });
  return { object_key: meta.object_key, storage_backend: meta.storage_backend || 'hetzner', bucket: meta.bucket };
}
